import { Layout } from 'layouts/Layout'
import { IconSvg } from '@prism/icon'
import shallow from 'zustand/shallow'
import { StyledHeaderActions, StyledStickyHeaderPage } from 'modules/VehicleList'
import { useFetch } from 'hooks/useFetch'
import { purchaseHistorySortOptions } from 'common/constants'
import { VehicleStickyBar } from 'modules/VehicleList/VehicleStickyBar'
import { PurchaseHistoryContext, PurchaseHistoryProvider } from 'contexts/PurchaseHistoryContext'
import { useDtmAnalytics } from 'hooks/useDtmAnalytics'
import { useContext, useEffect, useRef, useState } from 'react'
import { SearchSection } from 'modules/PurchaseHistory/SearchSection'
import { Loader, OverlayLoader } from 'components/Loader'
import { PurchaseHistoryListSection } from 'modules/PurchaseHistory/PurchaseHistoryListSection'
import { Link } from 'components/Share/Link'
import { formatDateString } from 'utils/dateUtils'
import { getManheimCheckoutURL } from 'apis/userApis'
import { Sort } from 'components/Sort/Sort'
import { ISortBy } from 'types/baseTypes'
import { setListingBreadcrumb } from 'utils/breadcrumbUtils'
import { useGlobalStore } from 'store/useGlobalStore'
import { useLocation } from 'react-router-dom'
import { Rules } from 'common/rules'
import { ArbitrationForm } from '../modules/PurchaseHistory/ArbitrationForm'
import { ArbitrationState, IArbitrationResult } from 'types/arbitrationTypes'
import { IPurchaseHistoryResult } from 'types/purchaseHistoryTypes'
import { ToastContextProvider } from 'contexts/ToastContext'

export const PurchaseHistoryContent = () => {
  const { isLoading, totalRecords, searchFilter, updateSearchFilter, arbitrationMetadata } =
    useContext(PurchaseHistoryContext)
  const getLocalText = useGlobalStore((state) => state.getLocalText)
  const location = useLocation()
  const { endPageLoad } = useDtmAnalytics()
  const hasTrackedPageLoad = useRef(false)

  useEffect(() => {
    if (!isLoading && !hasTrackedPageLoad.current) {
      endPageLoad()
      hasTrackedPageLoad.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  const [showArbitrationModal, setShowArbitrationModal] = useState(false)
  const [selectedVehicle, setSelectedVehicle] = useState<IPurchaseHistoryResult | null>(null)
  const [isUpdateAttachment, setIsUpdateAttachment] = useState(false)

  const handleOpenArbitrationModal = (vehicle: IPurchaseHistoryResult, updateAttachment: boolean) => {
    setSelectedVehicle(vehicle)
    setIsUpdateAttachment(updateAttachment)
    setShowArbitrationModal(true)
  }

  const handleCloseArbitrationModal = () => {
    setSelectedVehicle(null)
    setIsUpdateAttachment(false)
    setShowArbitrationModal(false)
  }

  const handleArbitrationSuccess = (result: IArbitrationResult, shouldRefreshList = true) => {
    if (!shouldRefreshList) {
      return
    }

    if (result.arbitrationState === ArbitrationState.Submitted) {
      handleCloseArbitrationModal()
    }

    updateSearchFilter({ ...searchFilter })
  }
  useEffect(() => {
    const pageTitle = getLocalText('PURCHASE_HISTORY', 'Purchase History')

    setListingBreadcrumb(pageTitle, location.pathname)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [isInRule] = useGlobalStore((state) => [state.isInRule], shallow)

  const getDownloadReportUrl = () => {
    const startDateValue = formatDateString(new Date(searchFilter?.FromDate))
    const endDateValue = formatDateString(new Date(searchFilter?.ToDate))
    const sortFieldName = searchFilter.SortBy.Value.split(' ')[0]
    return `/resource/DownloadPurchaseHistory?startDate=${startDateValue}&endDate=${endDateValue}&sortBy=${sortFieldName}&sortOrder=${searchFilter.SortBy.SortOrder}`
  }

  const handleSortChange = (selectedSortBy: ISortBy) => {
    updateSearchFilter({ ...searchFilter, SortBy: selectedSortBy })
  }

  const { loading: manHeimLoading, data: manHeimLink } = useFetch(getManheimCheckoutURL)

  return (
    <Layout title={'Purchase History'}>
      {isLoading && <OverlayLoader />}
      <StyledStickyHeaderPage>
        <VehicleStickyBar
          sectionTitle={'Purchase History'}
          itemCount={totalRecords}
          displayCollapse={false}
          rightActionSpan={8}
          rightActions={
            <StyledHeaderActions>
              <div className="view-icons">
                <Link to={getDownloadReportUrl()}>
                  <span className="right-action">
                    <IconSvg glyph="download" color="primary" />
                    &nbsp;Download Report&nbsp;
                  </span>
                </Link>
                {isInRule(Rules.VIEW_CHECKOUT_AT_MANHEIMCOM) && (
                  <>
                    {manHeimLoading ? (
                      <Loader />
                    ) : (
                      <a href={manHeimLink} target="_blank" rel="noopener noreferrer">
                        <span className="right-action">
                          <IconSvg glyph="link" color="primary" />
                          &nbsp;Check out at Manheim&nbsp;
                        </span>
                      </a>
                    )}
                  </>
                )}
              </div>
              <Sort
                currentSort={searchFilter.SortBy}
                sortOptions={purchaseHistorySortOptions}
                onChange={handleSortChange}
              />
            </StyledHeaderActions>
          }
        />
        <SearchSection />
        <PurchaseHistoryListSection onArbitrateClick={handleOpenArbitrationModal} />
      </StyledStickyHeaderPage>
      {showArbitrationModal && selectedVehicle && (
        <ArbitrationForm
          vehicleInstanceID={selectedVehicle.VehicleInstanceID}
          buyerVehiclePurchaseID={selectedVehicle.BuyerVehiclePurchaseID}
          vin={selectedVehicle.Vin}
          arbitrationState={selectedVehicle.ArbitrationState as ArbitrationState}
          isUpdateAttachment={isUpdateAttachment}
          metadata={arbitrationMetadata}
          onClose={handleCloseArbitrationModal}
          onSuccess={handleArbitrationSuccess}
        />
      )}
    </Layout>
  )
}

export const PurchaseHistory = () => {
  return (
    <PurchaseHistoryProvider>
      <ToastContextProvider>
        <PurchaseHistoryContent />
      </ToastContextProvider>
    </PurchaseHistoryProvider>
  )
}
